import { useState } from 'react';
import { Loader2, Zap } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Interval mode toggle for a STEADY cardio block.
 * Engine-made interval blocks already run the timer, so the row only appears on
 * steady blocks and on blocks the user turned to interval themselves (so it can
 * be turned back off).
 */
export function CardioIntervalToggle({ cardioBlock, planId, dayNumber, workoutSessionId, onUpdated, testId = 'cardio-interval-toggle' }) {
  const { api } = useAuth();
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState(null);

  const sessionType = cardioBlock?.session_type || cardioBlock?.type || 'steady';
  const isInterval = sessionType === 'interval';
  const userSelected = cardioBlock?.interval_mode_user_selected === true;

  if (!cardioBlock || !planId || !dayNumber) return null;
  if (isInterval && !userSelected) return null;
  if (!isInterval && sessionType !== 'steady') return null;

  const handleChange = async (next) => {
    setSaving(true);
    setNote(null);
    try {
      const res = await api.post(`/workouts/${planId}/cardio-interval-mode`, {
        day_number: dayNumber,
        enabled: next,
        workout_session_id: workoutSessionId || undefined,
      });
      if (res.data.applied) {
        onUpdated?.(res.data.cardio_block);
      } else {
        setNote(res.data.note || 'Intervals are not available for this session.');
      }
    } catch (err) {
      setNote('Could not change the session type — try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-white/5" data-testid={testId}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Zap size={13} className={isInterval ? 'text-orange-400' : 'text-white/40'} />
          <div className="min-w-0">
            <span className="block text-sm text-white font-heading font-black uppercase tracking-tight">Interval Mode</span>
            <span className="block text-xs text-[#A1A1AA] leading-tight">Work / rest rounds instead of one steady pace</span>
          </div>
        </div>
        {saving ? (
          <Loader2 size={18} className="text-accent animate-spin shrink-0" data-testid={`${testId}-spinner`} />
        ) : (
          <Switch
            checked={isInterval}
            onCheckedChange={handleChange}
            className="shrink-0 data-[state=checked]:bg-orange-500"
            data-testid={`${testId}-switch`}
            aria-label="Interval mode"
          />
        )}
      </div>
      {note && (
        <p className="mt-2 text-xs text-orange-300/80 leading-relaxed" data-testid={`${testId}-note`}>{note}</p>
      )}
    </div>
  );
}
